import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { Modal, Inp } from "./Enquiries";
import { Plus, Trash, Pencil, Stack } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

export default function TreatmentTemplates() {
  const { user } = useAuth();
  const canManage = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/tp-templates")).data);
  useEffect(() => {
    load();
  }, []);

  return (
    <div data-testid="templates-page">
      <PageHeader
        title="Treatment plan templates"
        subtitle="Multi-visit bundles you can apply to any patient"
        actions={
          canManage ? (
            <button
              onClick={() => setShowNew(true)}
              className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm"
              data-testid="add-template-btn"
            >
              <Plus size={16} weight="bold" /> New template
            </button>
          ) : null
        }
      />
      <div className="px-6 sm:px-10 py-8">
        {!canManage ? (
          <div className="mb-4 bg-zinc-50 border border-zinc-200 rounded-sm p-4 text-sm text-zinc-600">
            Templates are read-only for your role.
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.length === 0 ? (
            <div className="col-span-full text-sm text-zinc-500 text-center py-10">
              No templates yet. Create one like an implant package with multiple visits.
            </div>
          ) : null}

          {items.map((template) => {
            const totalCost = (template.items || []).reduce((sum, item) => sum + (item.default_cost || 0), 0);
            return (
              <div key={template.id} className="bg-white border border-zinc-200 rounded-sm p-5" data-testid={`tmpl-${template.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <Stack size={20} weight="duotone" color="#E60000" />
                    <h3 className="font-bold text-zinc-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                      {template.name}
                    </h3>
                  </div>
                  {canManage ? (
                    <button
                      onClick={() => setEditing(template)}
                      className="text-zinc-500 hover:text-[#E60000]"
                      data-testid={`tmpl-edit-${template.id}`}
                    >
                      <Pencil size={14} weight="bold" />
                    </button>
                  ) : null}
                </div>
                {template.description ? <p className="text-xs text-zinc-500 mt-1">{template.description}</p> : null}
                <div className="mt-3 space-y-1">
                  {(template.items || []).map((item, idx) => (
                    <div key={idx} className="text-xs flex justify-between border-l-2 border-zinc-200 pl-2">
                      <span className="text-zinc-700">{idx + 1}. {item.title}</span>
                      {item.default_cost ? <span className="text-zinc-500">Rs {item.default_cost}</span> : null}
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center justify-between">
                  <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
                    {(template.items || []).length} visits
                  </span>
                  <span className="text-sm font-bold text-zinc-900">Rs {totalCost.toFixed(0)}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showNew && canManage ? (
        <TemplateModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />
      ) : null}
      {editing && canManage ? (
        <TemplateModal item={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      ) : null}
    </div>
  );
}

function TemplateModal({ item, onClose, onSaved }) {
  const isEdit = !!item;
  const [name, setName] = useState(item?.name || "");
  const [description, setDescription] = useState(item?.description || "");
  const [items, setItems] = useState(
    item?.items?.length ? [...item.items] : [{ title: "", description: "", default_cost: 0, sequence: 0 }]
  );
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name) {
      toast.error("Name required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name,
        description: description || null,
        items: items.map((entry, idx) => ({
          title: entry.title,
          description: entry.description || null,
          default_cost: Number(entry.default_cost) || 0,
          sequence: idx,
        })),
      };
      if (isEdit) await api.patch(`/tp-templates/${item.id}`, payload);
      else await api.post("/tp-templates", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/tp-templates/${item.id}`);
    toast.success("Deleted");
    onSaved();
  };

  const updateItem = (index, patch) => setItems((state) => state.map((entry, idx) => (idx === index ? { ...entry, ...patch } : entry)));

  return (
    <Modal onClose={onClose} title={isEdit ? "Edit template" : "New template"}>
      <Inp label="Template name" v={name} onChange={setName} testid="tmpl-name" />
      <div className="mt-3">
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Description</label>
        <textarea
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900"
          placeholder="Example: four-visit implant package"
          data-testid="tmpl-desc"
        />
      </div>

      <div className="mt-4">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700 mb-2">Visits / items</div>
        <div className="space-y-2">
          {items.map((entry, index) => (
            <div key={index} className="grid grid-cols-12 gap-2 items-center">
              <div className="col-span-1 text-center font-black text-zinc-400" style={{ fontFamily: "Manrope, sans-serif" }}>
                {index + 1}
              </div>
              <input
                value={entry.title}
                onChange={(e) => updateItem(index, { title: e.target.value })}
                className="col-span-6 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-900"
                placeholder="Visit title"
                data-testid={`tmpl-item-title-${index}`}
              />
              <input
                type="number"
                value={entry.default_cost}
                onChange={(e) => updateItem(index, { default_cost: e.target.value })}
                className="col-span-3 border border-zinc-300 rounded-sm px-2 py-1.5 text-sm focus:outline-none focus:border-zinc-900"
                placeholder="Rs cost"
                data-testid={`tmpl-item-cost-${index}`}
              />
              <button onClick={() => setItems((state) => state.filter((_, idx) => idx !== index))} className="col-span-2 text-zinc-400 hover:text-[#E60000] inline-flex items-center justify-center">
                <Trash size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
        <button onClick={() => setItems([...items, { title: "", description: "", default_cost: 0 }])} className="mt-2 text-xs uppercase tracking-[0.2em] font-bold text-[#E60000]" data-testid="tmpl-add-item">
          + Add visit
        </button>
      </div>

      <div className="mt-5 flex items-center justify-between">
        {isEdit ? (
          <button onClick={remove} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000] inline-flex items-center gap-1" data-testid="tmpl-delete">
            <Trash size={14} /> Delete
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">
            Cancel
          </button>
          <button onClick={save} disabled={busy} className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60" data-testid="tmpl-save">
            {busy ? "Saving..." : "Save template"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
